import { useFormik } from "formik";
import { useRouter } from "next/router";
import { Button, Dropdown, Form } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { FaAngleRight } from "react-icons/fa";
import * as Yup from "yup";
import { countries } from "../../constants";

export default function GetInTouchPopUp({ submitGetInTouch, handleClick }) {
  const router = useRouter();
  const isExport =
    router.asPath.includes("/tile-exports") ||
    router.asPath.includes("/download-export-catalogue");

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .matches(/^[a-zA-Z\s]+$/, "Name should only contain letters and spaces"),

    countryCode: isExport
      ? Yup.string().required("Please select a country code")
      : Yup.string().nullable(),

    cc: isExport
      ? Yup.string().required("Missing country flag info")
      : Yup.string().nullable(),

    number: Yup.string()
      .length(10, "Invalid number")
      .matches(/^[a-zA-Z0-9]+$/, "Mobile number must be alphanumeric")
      .matches(/^\d+$/, "Mobile number must be numeric")
      .required("Mobile number is required"),

    email: isExport
      ? Yup.string().email("Invalid Email format").required("Email is required")
      : Yup.string().nullable(),

    pincode: Yup.string().nullable().length(6, "Invalid pincode"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      number: "",
      email: "",
      pincode: "",
      countryCode: "",
      cc: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting, setErrors }) => {
      try {
        await submitGetInTouch("Get In Touch", values, "GetInTouchPopUp");
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrors({ general: "Something went wrong. Please try again." });
      } finally {
        setSubmitting(false);
        resetForm();
      }
    },
  });

  const handleCountrySelect = (code) => {
    const country = countries.find((c) => c.code === code);
    if (country) {
      formik.setFieldValue("countryCode", country.code);
      formik.setFieldValue("cc", country.cc);
    }
  };

  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="close-form" onClick={handleClick}>
        <FaAngleRight />
      </div>

      {/* Name */}
      <Form.Group className="mb-3">
        <Form.Control
          type="text"
          name="name"
          placeholder="Name*"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          isInvalid={formik.touched.name && formik.errors.name}
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.name}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Mobile Number */}
      <Form.Group className="mb-3">
        {isExport ? (
          <div className="export-page-getInTouch">
            <Dropdown onSelect={handleCountrySelect}>
              <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                {formik.values.countryCode ? (
                  <div>
                    <ReactCountryFlag countryCode={formik.values.cc} svg /> (
                    {formik.values.countryCode})
                  </div>
                ) : (
                  "Country Code"
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
                {countries.map((country, index) => (
                  <Dropdown.Item key={index} eventKey={country.code}>
                    <ReactCountryFlag countryCode={country.cc} svg />{" "}
                    {country.name} ({country.code})
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            {formik.touched.countryCode && formik.errors.countryCode && (
              <div className="text-danger">{formik.errors.countryCode}</div>
            )}

            <Form.Control
              type="number"
              name="number"
              placeholder="Mobile Number*"
              value={formik.values.number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.number && formik.errors.number}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.number}
            </Form.Control.Feedback>
          </div>
        ) : (
          <>
            <Form.Control
              type="number"
              name="number"
              placeholder="Mobile Number*"
              value={formik.values.number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.number && formik.errors.number}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.number}
            </Form.Control.Feedback>
          </>
        )}
      </Form.Group>

      {/* Email / Pincode */}
      <Form.Group className="mb-3">
        {isExport ? (
          <>
            <Form.Control
              type="email"
              name="email"
              placeholder="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.email && formik.errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.email}
            </Form.Control.Feedback>
          </>
        ) : (
          <>
            <Form.Control
              type="number"
              name="pincode"
              placeholder="Pincode (Optional)"
              value={formik.values.pincode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.pincode && formik.errors.pincode}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.pincode}
            </Form.Control.Feedback>
          </>
        )}
      </Form.Group>

      {/* General Error & Submit */}
      {formik.errors.general && (
        <p className="text-danger">{formik.errors.general}</p>
      )}
      <Button
        variant="primary"
        type="submit"
        disabled={formik.isSubmitting}
        style={{ width: "100%" }}
      >
        {formik.isSubmitting ? "Submitting..." : "Submit"}
      </Button>
    </Form>
  );
}
